type PriceInput = {
  type: 'custom' | 'fixed' | 'from' | 'range';
  currency?: string | null;
  from?: number | null;
  to?: number | null;
  value?: number | null;
};

type FormatPriceResult = {
  amount: string;
  formatted: string;
  symbol: string;
};

const getCurrencySymbol = (currencyCode: string, locale = 'uk-UA'): string => {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      style: 'currency',
    });

    const parts = formatter.formatToParts(0);
    const currencyPart = parts.find((part) => part.type === 'currency');

    return currencyPart?.value ?? currencyCode;
  } catch {
    return currencyCode;
  }
};

const formatNumber = (value: number, locale = 'uk-UA'): string =>
  new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);

const formatAmount = (price: PriceInput, fromLabel: string, locale: string): string => {
  switch (price.type) {
    case 'fixed':
    case 'custom':
      return price.value != null ? formatNumber(price.value, locale) : '';

    case 'from':
      return price.from != null ? `${fromLabel} ${formatNumber(price.from, locale)}` : '';

    case 'range':
      if (price.from != null && price.to != null) {
        return `${formatNumber(price.from, locale)} – ${formatNumber(price.to, locale)}`;
      }

      return price.from != null ?
          `${fromLabel} ${formatNumber(price.from, locale)}`
        : formatNumber(price.to ?? 0, locale);
  }
};

type FormatPriceOptions = {
  /** Label for "from" type prices (defaults to "від") */
  fromLabel?: string;
  /** Locale for number formatting (defaults to "uk-UA") */
  locale?: string;
};

/**
 * Format price object into readable strings with currency symbol
 *
 * Handles different price types:
 * - fixed: single value (e.g., "100")
 * - from: starting price (e.g., "від 100" / "from 100")
 * - range: price range (e.g., "100 – 200")
 * - custom: treated as fixed if value exists
 *
 * @param price - Price object with type, value, from, to, currency
 * @param options - Formatting options
 * @param options.fromLabel - Label for "from" type prices (defaults to "від")
 * @param options.locale - Locale for number formatting (defaults to "uk-UA")
 *
 * @returns Object with symbol (currency symbol), amount, and formatted strings
 *
 * @example
 * ```ts
 * // Fixed price
 * formatPrice({ type: 'fixed', value: 1000, currency: 'UAH' })
 * // => { symbol: '₴', amount: '1 000', formatted: '₴ 1 000' }
 *
 * // From price
 * formatPrice({ type: 'from', from: 1000, currency: 'UAH' })
 * // => { symbol: '₴', amount: 'від 1 000', formatted: '₴ від 1 000' }
 *
 * // Range price
 * formatPrice({ type: 'range', from: 1000, to: 2000, currency: 'UAH' })
 * // => { symbol: '₴', amount: '1 000 – 2 000', formatted: '₴ 1 000 – 2 000' }
 *
 * // USD
 * formatPrice({ type: 'fixed', value: 100, currency: 'USD' })
 * // => { symbol: '$', amount: '100', formatted: '$ 100' }
 *
 * // EUR
 * formatPrice({ type: 'fixed', value: 100, currency: 'EUR' })
 * // => { symbol: '€', amount: '100', formatted: '€ 100' }
 * ```
 */
export const formatPrice = (price: PriceInput, options: FormatPriceOptions = {}): FormatPriceResult => {
  const { fromLabel = 'від', locale = 'uk-UA' } = options;
  const symbol = price.currency ? getCurrencySymbol(price.currency, locale) : '';
  const amount = formatAmount(price, fromLabel, locale);
  const formatted = symbol && amount ? `${symbol} ${amount}` : amount || symbol;

  return { amount, formatted, symbol };
};
